import { broadcastResponseToMainFrame } from '@azure/msal-browser/redirect-bridge';
import { runPopupRelay } from '@azure/msal-browser/popup-relay';

// When this window is an MSAL auth-response landing (login popup or silent-SSO
// iframe returning to the redirect URI), run the redirect bridge to broadcast
// the response to the opener/main frame and close. This must happen BEFORE
// Angular bootstraps, otherwise the hash router (useHash) overwrites the
// response fragment and the opener fails with no_token_request_cache_error.
const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
const queryParams = new URLSearchParams(window.location.search.replace(/^\?/, ''));

// MSAL opens the same-origin relay page with the authorize request in `req`.
// runPopupRelay() validates `window.opener` itself, so don't gate on it here -
// otherwise the relay page silently bootstraps the app instead of relaying.
const isPopupRelay = hashParams.has('req') || queryParams.has('req');
// MSAL marks an auth-response landing with a `state` param (hash or query);
// the payload may be a code, an error, or an EAR ear_jwe.
const hasAuthResponse = hashParams.has('state') || queryParams.has('state');
const isNestedWindow = !!window.opener || window !== window.parent;

if (isPopupRelay) {
  // Top-level relay popup opened by an embedded (cross-origin) iframe: open the
  // IdP child popup and relay its response back to the iframe via postMessage.
  // Open the child exactly over this relay window so the user sees a single
  // login popup instead of two stacked windows.
  runPopupRelay({
    popupWindowAttributes: {
      popupSize: {
        width: window.outerWidth || 520,
        height: window.outerHeight || 640
      },
      popupPosition: {
        top: window.screenY || 0,
        left: window.screenX || 0
      }
    }
  });
} else if (hasAuthResponse && isNestedWindow) {
  broadcastResponseToMainFrame().catch(err =>
    console.error('MSAL redirect bridge error:', err)
  );
} else {
  // Self-heal: an interrupted popup/redirect can leave a stale
  // 'msal.interaction.status' flag in sessionStorage, which makes
  // handleRedirectPromise fail repeatedly with no_token_request_cache_error
  // (the rejection is memoized on the singleton, causing an infinite guard loop).
  // The top-level app window never completes an interaction itself (popups and
  // iframes do, via the bridge above), so clearing a leftover flag here is safe.
  try {
    window.sessionStorage.removeItem('msal.interaction.status');
  } catch {
    /* sessionStorage unavailable - ignore */
  }

  // Load Angular only in the real app window. The auth-bridge / relay popups
  // short-circuit above, so they never download the full app bundle and login
  // stays fast. main.js stays tiny, so the app window is not delayed.
  Promise.all([
    import('@angular/platform-browser'),
    import('./app/app.module')
  ])
    .then(([{ platformBrowser }, { AppModule }]) =>
      platformBrowser().bootstrapModule(AppModule)
    )
    .catch(err => console.error(err));
}

